import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS } from '../business';
import { config } from '../config';

export type LegalKind = 'terms' | 'privacy';

/** 시행일·버전. 개정하면 이전 버전을 보관하고 시행 7일 전(회원에게 불리하면 30일 전) 공지한다. */
const EFFECTIVE = '2026년 11월 1일';
const DRAFT = '초안 — 법률 검토 전';
const co = () => BUSINESS.name || 'SelfOn 운영사(상호 등록 예정)';

const Art = ({ n, title, children }: { n: number | string; title: string; children: ReactNode }) => (
  <section className="legal-art"><h3>제{n}조 ({title})</h3>{children}</section>
);

/**
 * 이용약관(공정위 전자상거래 표준약관 구성을 센터용 SaaS 구독·알림 메시지에 맞게 고침)·개인정보처리방침(개인정보보호위원회 작성지침 항목).
 * 공개 전에 법률 검토가 필요하다. 실제 처리 현황이 바뀌면(수탁사 추가·보유 기간 변경 등) 반드시 함께 고친다.
 */
export function Legal({ kind, onBack }: { kind: LegalKind; onBack?: () => void }) {
  return (
    <article className="legal stack">
      <h2>{kind === 'terms' ? 'SelfOn 이용약관' : 'SelfOn 개인정보처리방침'}</h2>
      <p className="muted small">시행일: {EFFECTIVE} ({DRAFT})</p>
      {kind === 'terms' ? <Terms /> : <Privacy />}
      {onBack && <button type="button" className="btn btn-ghost" onClick={onBack}>돌아가기</button>}
    </article>
  );
}

function Terms() {
  return <>
    <h3 className="legal-chapter">제1장 총칙</h3>
    <Art n={1} title="목적">
      <p>이 약관은 {co()}(이하 "회사")가 제공하는 발달재활·심리상담센터 통합 관리 서비스 SelfOn(웹·모바일 앱·PC 앱을 포함하며, 이하 "서비스")의 이용 조건과 절차, 회사와 회원의 권리·의무 및 책임 사항을 정합니다.</p>
    </Art>
    <Art n={2} title="정의">
      <ol>
        <li>"회원": 이 약관에 동의하고 서비스에 가입한 자로서 원장, 선생님, 보호자, 학생으로 나뉩니다.</li>
        <li>"센터": 원장이 서비스에 개설한 기관 단위 공간입니다.</li>
        <li>"원장": 센터를 대표하여 서비스를 계약·관리하고 유료서비스를 결제하며 해지할 권한을 가진 회원입니다.</li>
        <li>"선생님": 센터 가입 코드로 센터에 소속되어 일정·출결·과제를 관리하는 치료사·상담사 회원입니다.</li>
        <li>"보호자": 만 19세 미만 자녀를 등록하고 센터와 연결하여 과제·동의를 관리하는 회원입니다.</li>
        <li>"학생": 만 14세 이상으로서 직접 가입한 회원입니다(가입 화면의 "일반인"). 만 14세 미만 아동은 보호자 계정의 "자녀 모드"로만 이용합니다.</li>
        <li>"이용자": 센터를 이용하는 아동·내담자로서 센터가 서비스에 등록한 사람을 말하며, "이용자 정보"는 센터가 서비스에 입력하는 이용자에 관한 정보입니다.</li>
        <li>"계정": 회원을 식별하기 위해 회원이 등록한 이메일과 비밀번호입니다.</li>
        <li>"무료체험": 유료서비스에 앞서 요금 없이 <Link to="/pricing">요금 안내</Link>에 적힌 기능을 써 보는 이용 형태입니다.</li>
        <li>"유료서비스": 월 요금제·추가 기능·학생 월 이용권·포인트 등 대가를 받고 제공하는 서비스입니다.</li>
        <li>"포인트": 알림톡·문자 발송에 쓰기 위해 센터가 미리 충전하는 서비스 내 결제 수단으로, 1포인트는 1원입니다.</li>
        <li>"알림 메시지": 센터가 서비스를 통해 보호자·이용자·선생님에게 보내는 알림톡·문자메시지입니다.</li>
      </ol>
    </Art>
    <Art n={3} title="약관의 게시와 개정">
      <ol>
        <li>회사는 이 약관과 상호, 대표자, 사업장 주소, 사업자등록번호, 통신판매업 신고번호, 연락처를 서비스 첫 화면과 공개 페이지에 게시합니다.</li>
        <li>회사는 관계 법령을 위반하지 않는 범위에서 약관을 개정할 수 있으며, 개정 내용과 사유, 시행일을 시행 7일 전부터 서비스에 공지합니다. 회원에게 불리하거나 중요한 변경은 시행 30일 전부터 공지하고 등록된 이메일로도 개별 통지합니다.</li>
        <li>회사가 제2항을 알리면서 시행일까지 거부 의사를 밝히지 않으면 동의한 것으로 본다는 뜻을 함께 알렸는데도 회원이 거부 의사를 밝히지 않으면 개정 약관에 동의한 것으로 봅니다. 동의하지 않는 회원은 이용계약을 해지할 수 있으며, 이 경우 환불은 <Link to="/refund">환불 정책</Link>을 따릅니다.</li>
      </ol>
    </Art>
    <Art n={4} title="개별 안내와 약관 외 준칙">
      <ol>
        <li>요금, 환불, 개인정보 처리는 <Link to="/pricing">요금 안내</Link>, <Link to="/refund">환불 정책</Link>, <Link to="/privacy">개인정보처리방침</Link>을 따릅니다. 특정 기능에 별도의 조건을 둘 때에는 그 기능을 처음 쓸 때 안내하고 동의를 받으며, 약관과 다르면 그 조건이 우선합니다.</li>
        <li>이 약관에 정하지 않은 사항은 「전자상거래 등에서의 소비자보호에 관한 법률」, 「약관의 규제에 관한 법률」, 「개인정보 보호법」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」(이하 "정보통신망법"), 「전기통신사업법」 등 관계 법령과 일반 상관례를 따릅니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제2장 이용계약</h3>
    <Art n={5} title="회원가입과 계약의 성립">
      <ol>
        <li>가입을 원하는 자는 역할을 고르고 이 약관과 개인정보처리방침에 동의한 뒤 정보를 입력하고 이메일 인증을 마쳐야 합니다. 이용계약은 회사가 가입을 승낙하면 성립합니다.</li>
        <li>선생님은 소속 센터의 가입 코드가 필요하며, 원장은 가입과 함께 센터를 무료체험으로 개설합니다.</li>
        <li>만 14세 미만은 직접 가입할 수 없습니다. 보호자가 자녀로 등록하고 필요한 동의를 한 경우에만 자녀 모드로 이용할 수 있습니다.</li>
        <li>회원은 자신의 실제 정보를 입력해야 합니다.</li>
      </ol>
    </Art>
    <Art n={6} title="가입의 거절과 유보">
      <ol>
        <li>회사는 다음의 경우 가입을 거절하거나, 가입 후 알게 되면 이용계약을 해지할 수 있습니다.
          <ul>
            <li>타인의 정보를 쓰거나 허위 정보를 입력한 경우</li>
            <li>만 14세 미만이 직접 가입한 경우</li>
            <li>법령이나 이 약관 위반으로 이용이 제한된 지 3개월이 지나지 않은 경우</li>
            <li>위법한 목적으로 서비스를 이용하려는 경우</li>
          </ul>
        </li>
        <li>설비에 여유가 없거나 장애가 있는 경우 그 사유가 해소될 때까지 승낙을 미룰 수 있습니다.</li>
        <li>거절하거나 미룬 경우 회사는 그 사유를 신청자에게 알립니다.</li>
      </ol>
    </Art>
    <Art n={7} title="회원정보의 변경">
      <p>회원은 가입 때 입력한 정보가 바뀌면 지체 없이 서비스에서 고치거나 회사에 알려야 합니다. 특히 원장은 통지를 받을 이메일과 사업자 정보를 최신으로 유지해야 합니다. 알리지 않아 생긴 불이익에 대해서는 회사에 고의 또는 과실이 없는 한 회사가 책임지지 않습니다.</p>
    </Art>
    <Art n={8} title="계정 관리">
      <ol>
        <li>계정과 비밀번호의 관리 책임은 회원에게 있으며, 타인에게 빌려주거나 공유할 수 없습니다. 센터 구성원도 각자의 계정을 써야 합니다.</li>
        <li>회원은 계정 도용을 알게 되면 즉시 회사에 알리고 회사의 안내를 따라야 합니다.</li>
        <li>서비스는 30분 동안 사용이 없으면 자동으로 로그아웃합니다. 회사는 OTP 등 추가 인증을 제공할 수 있으며, 민감정보를 다루는 원장과 선생님에게 사용을 권장합니다.</li>
      </ol>
    </Art>
    <Art n={9} title="회원에 대한 통지">
      <ol>
        <li>회사는 회원이 등록한 이메일, 서비스 내 공지, 알림으로 통지합니다.</li>
        <li>센터 전체에 관한 통지는 원장에게 하면 되고, 원장은 필요한 내용을 소속 선생님에게 전달합니다.</li>
        <li>불특정 다수에 대한 통지는 서비스에 7일 이상 게시하는 것으로 갈음할 수 있습니다. 다만 회원에게 불리하거나 중요한 사항은 개별 통지합니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제3장 서비스 이용</h3>
    <Art n={10} title="서비스의 내용">
      <ol>
        <li>회사는 다음 서비스를 제공합니다: 일정·출결 관리, 이용자 관리, 수납·바우처 대조, 월 결산·회의록, 치료 과제(보호자·자녀 수행과 평가), AI 기록 초안, 센터 수업 안내, 선생님 찾기(연결 서비스), 공지·문의, 알림 메시지, 할 일 관리, 그 밖에 회사가 추가로 개발하는 기능.</li>
        <li>요금제에 따라 쓸 수 있는 기능이 다르며, 무료체험은 요금 안내에 적힌 기능만 제공합니다.</li>
      </ol>
    </Art>
    <Art n={11} title="서비스의 변경">
      <ol>
        <li>회사는 운영·기술상 필요에 따라 서비스를 변경할 수 있으며, 변경 내용과 적용일을 미리 공지합니다. 중요한 변경은 7일 전에, 유료서비스의 주요 기능을 없애거나 줄이는 변경은 30일 전에 공지합니다.</li>
        <li>유료서비스의 주요 기능이 없어져 계속 이용하기 어려운 원장은 계약을 해지할 수 있고, 남은 기간의 요금을 환불받을 수 있습니다.</li>
      </ol>
    </Art>
    <Art n={12} title="일시 중단과 보상">
      <ol>
        <li>회사는 설비 점검·교체, 장애, 해킹 등 침해사고 대응, 통신 두절, 천재지변 등의 사유로 서비스를 일시 중단할 수 있습니다. 계획된 점검은 미리 공지하고, 미리 알릴 수 없었던 경우에는 지체 없이 사후에 알립니다.</li>
        <li>회사의 책임 있는 사유로 유료서비스가 사전 고지 없이 연속 24시간 이상 중단되거나, 사전 고지했더라도 연속 10시간을 넘겨 중단되면 중단된 시간만큼 이용 기간을 무료로 연장합니다. 회원의 다른 손해배상 청구권은 제한되지 않습니다.</li>
      </ol>
    </Art>
    <Art n={13} title="서비스의 종료">
      <p>회사가 사업 종료 등으로 서비스를 종료할 때는 30일 전에 알리고, 센터가 자료를 내려받을 수 있는 기간을 주며, 남은 유료 기간과 포인트는 환불 정책에 따라 환불합니다.</p>
    </Art>
    <Art n={14} title="AI 기능">
      <ol>
        <li>AI 기록 초안은 이름 등을 자동으로 가린 내용을 외부 AI 처리자에게 보내 만들며, 보호자가 해당 국외 이전("AI 초안" 또는 "AI 회기 기록 초안")에 동의한 이용자에 대해서만 사용할 수 있습니다. 음성 전사는 "음성 인식(국외 이전)" 동의가 따로 필요합니다.</li>
        <li>AI 초안은 참고용이며, 선생님이 검토·수정한 뒤에만 기록으로 저장됩니다. 진단·처방·예후를 판단하는 도구가 아닙니다.</li>
      </ol>
    </Art>
    <Art n={15} title="참고용 기능">
      <ol>
        <li>바우처 대조는 승인 내역과 출결을 비교한 "의심" 표시이며, 부정결제 여부의 최종 판단은 센터와 관계 기관의 확인 절차로 합니다. 회사는 대조 결과만으로 어떤 사실도 확정하지 않습니다.</li>
        <li>월 결산과 세무 관련 화면은 센터가 입력한 자료를 정리해 보여 주는 참고 자료입니다. 회계 처리와 세금 신고의 책임은 센터에 있습니다.</li>
        <li>수업 커리큘럼 구성 예시는 담당 선생님이 검토할 초안이며, 확정된 수업 계획이 아닙니다.</li>
      </ol>
    </Art>

    <Art n="15의2" title="선생님 찾기(연결 서비스)">
      <ol>
        <li>선생님 찾기는 보호자와 공개된 선생님을 이어 주는 서비스이며, 회사는 수업 계약의 당사자가 아닙니다. 수업 내용, 일정, 비용, 환불은 보호자와 선생님(소속 센터가 있으면 그 센터)이 직접 정하고, 회사는 그 대금을 받거나 전달하지 않습니다.</li>
        <li>선생님 소개는 선생님이 직접 작성합니다. 소속 센터 원장 또는 회사가 공개를 승인하지만, 이는 자격이나 수업 품질을 보증하는 것이 아닙니다. 보호자는 첫 상담에서 자격을 확인해야 합니다.</li>
        <li>선생님은 자격, 경력, 소속을 사실대로 적어야 합니다. 거짓 내용이 확인되거나 아동 안전에 우려가 있으면 회사는 공개를 내리고 이용을 제한할 수 있습니다.</li>
        <li>선생님은 수락으로 받은 보호자 연락처와 아이 이름을 수업 상담과 진행 목적으로만 쓰며, 수업이 끝나면 따로 보관한 정보를 파기해야 합니다.</li>
        <li>수업과 관련한 사고나 분쟁의 책임은 그 수업의 당사자에게 있습니다. 다만 회사의 고의 또는 과실로 인한 손해는 그러하지 않습니다. 회원은 부적절한 선생님이나 이용자를 고객센터로 신고할 수 있습니다.</li>
      </ol>
    </Art>

    <Art n="15의3" title="할 일 관리">
      <ol>
        <li>할 일 관리는 회원 본인의 업무 목록입니다. 회원이 적은 할 일은 본인만 볼 수 있으며, 원장·본사·보호자도 볼 수 없습니다.</li>
        <li>원장은 소속 선생님에게 할 일을 맡길 수 있습니다. 맡기면 제목·메모·체크리스트·마감이 그 선생님에게 전달되고, 원장은 끝났는지만 알 수 있습니다.</li>
        <li>회원은 할 일에 이용자·아동·보호자 등 다른 사람의 개인정보를 되도록 적지 않아야 합니다. 원장은 그 이용자를 담당하지 않는 선생님에게 맡기는 할 일에 이용자 정보를 적어서는 안 되며, 이용자 기록은 이용자·회기 기록 기능에 남겨야 합니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제4장 유료서비스</h3>
    <Art n={16} title="요금과 결제">
      <ol>
        <li>유료서비스의 요금은 요금 안내에 게시하며 부가세를 포함합니다. 요금을 바꿀 때는 30일 전에 알리고, 이미 결제한 기간에는 적용하지 않습니다.</li>
        <li>학생 월 이용권을 제외한 유료서비스는 원장만 신청하고 결제할 수 있습니다.</li>
        <li>결제는 회사가 정한 결제대행사(PG)를 통한 신용·체크카드 등으로 합니다. 회사는 카드번호를 저장하지 않으며, 정기결제의 경우 결제대행사가 발급한 결제 키만 암호화해 보관합니다.</li>
        <li>요금제는 신청한 주기(매월 또는 매년)에 따라 결제일에 자동 결제됩니다. 결제가 실패하면 회사는 원장에게 알리고 다시 시도하며, 통지 후 14일이 지나도록 결제되지 않으면 유료 기능 이용을 제한할 수 있습니다. 이 경우에도 센터는 자료를 조회하고 내려받을 수 있습니다.</li>
        <li>학생 월 이용권은 학생과 연결된 보호자가 결제합니다. 자동 결제가 아니며, 미성년자의 이용은 결제한 보호자가 법정대리인으로서 동의한 것으로 봅니다.</li>
      </ol>
    </Art>
    <Art n={17} title="포인트">
      <ol>
        <li>포인트는 충전한 센터에서 알림 메시지 발송에만 쓸 수 있고, 다른 센터나 타인에게 넘길 수 없습니다. 이자는 붙지 않습니다.</li>
        <li>포인트는 메시지를 보낼 때 차감됩니다. 서비스의 문제로 발송이 실패하면 차감된 포인트를 되돌려 드립니다. 정상 발송 후 수신자의 차단 설정이나 단말기 문제로 받지 못한 경우는 되돌리지 않습니다.</li>
        <li>유효기간과 환불은 환불 정책을 따릅니다.</li>
      </ol>
    </Art>
    <Art n={18} title="청약철회·해지·환불">
      <p>청약철회, 해지, 환불의 기준과 절차는 <Link to="/refund">환불 정책</Link>을 따르며, 관계 법령이 회원에게 더 유리한 경우 그 법령을 따릅니다.</p>
    </Art>
    <Art n={19} title="요금 이의신청">
      <ol>
        <li>원장은 청구된 요금에 이의가 있으면 청구일부터 3개월 안에 고객센터에 신청할 수 있습니다. 이 기간이 지나도 법령상 인정되는 권리는 제한되지 않습니다.</li>
        <li>회사는 접수 후 14일 안에 조사 결과를 알립니다. 그 안에 끝내기 어려우면 사유와 처리 예정일을 알립니다.</li>
        <li>잘못 청구된 금액은 전액 돌려드립니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제5장 개인정보와 자료</h3>
    <Art n={20} title="개인정보 보호">
      <p>회사는 관계 법령과 <Link to="/privacy">개인정보처리방침</Link>에 따라 회원의 개인정보를 보호합니다. 법령에 따른 적법한 요청이 있는 경우가 아니면 동의 없이 목적 외로 이용하거나 제3자에게 제공하지 않습니다.</p>
    </Art>
    <Art n={21} title="센터 이용자 정보의 처리 위탁">
      <ol>
        <li>센터가 입력하는 이용자 정보에 대해서는 센터(원장)가 「개인정보 보호법」상 개인정보처리자이며, 회사는 센터로부터 처리를 위탁받은 수탁자입니다. 원장은 서비스 이용계약 체결로 이 조의 위탁에 동의합니다.</li>
        <li>센터는 이용자 정보를 적법하게 수집하고(민감정보·만 14세 미만 아동 정보는 별도 동의 포함) 필요한 범위에서만 입력할 책임이 있습니다. 또한 업무를 서비스에 위탁한다는 사실과 위탁하는 항목을 이용자나 보호자가 알 수 있도록 공개해야 합니다.</li>
        <li>회사는 위탁받은 목적(서비스 제공) 외로 이용자 정보를 처리하지 않고, 암호화·접근 통제·접속기록 보관 등 안전성 확보 조치를 하며, 재위탁 시 센터에 알립니다. 회사 직원(본사)이 센터 화면에 들어가는 것은 센터의 요청·문의 처리 등 필요한 경우에 한하며, 사유와 모든 동작이 센터 감사 로그에 남습니다.</li>
      </ol>
    </Art>
    <Art n={22} title="센터 내 권한">
      <ol>
        <li>원장은 센터 안에서 선생님이 입력한 기록과 활동 기록(감사 로그)을 볼 수 있습니다. 원장은 소속 선생님에게 이 사실을 미리 알려야 합니다.</li>
        <li>원장은 선생님이 퇴사하는 등 소속이 끝나면 지체 없이 권한을 해제해야 합니다. 선생님이 센터 업무로 입력한 기록은 센터의 자료로 남습니다.</li>
        <li>센터의 운영자가 바뀌는 경우 현재 원장과 새 운영자가 함께 회사에 알려야 합니다. 센터 운영권을 두고 다툼이 있으면 회사는 자료 보호를 위해 분쟁이 정리될 때까지 원장 권한 변경, 자료 내려받기 등을 제한할 수 있습니다.</li>
      </ol>
    </Art>
    <Art n={23} title="자료와 지식재산권">
      <ol>
        <li>회원이 입력·업로드한 자료(과제 결과물 사진 등)의 권리는 회원 또는 센터에 있습니다. 회사는 서비스 제공에 필요한 범위에서만 이용합니다.</li>
        <li>서비스 화면·소프트웨어·로고 등에 대한 권리는 회사에 있으며, 회원은 회사의 허락 없이 복제·배포·변형하거나 역설계할 수 없습니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제6장 알림 메시지</h3>
    <Art n={24} title="알림 메시지 발송">
      <ol>
        <li>알림 메시지는 일정 변경, 공지 등 센터 운영에 필요한 안내를 위해 보냅니다. 발송 내용과 수신 대상에 대한 책임은 메시지를 보낸 센터에 있습니다.</li>
        <li>센터는 수신자가 보낸 곳을 알 수 있는 발신 프로필을 쓰고, 「전기통신사업법」에 따라 미리 등록하고 인증한 발신번호로만 보내야 합니다.</li>
        <li>영리 목적의 광고성 정보를 보낼 때에는 정보통신망법에 따라 수신자의 사전 동의를 받고, (광고) 표시 등 법령이 정한 사항을 지켜야 합니다.</li>
      </ol>
    </Art>
    <Art n={25} title="불법 메시지에 대한 조치">
      <ol>
        <li>회원은 불법 스팸, 피싱, 스미싱, 사기·협박·음란 등 불법 정보, 발신번호를 거짓으로 표시한 메시지를 보내서는 안 됩니다.</li>
        <li>회사는 이런 메시지를 막기 위해 특정 내용의 발송을 제한하거나 하루 발송량을 제한할 수 있고, 수신자의 신고나 관계 기관의 요청이 있으면 발송 이력을 확인할 수 있습니다.</li>
        <li>불법 발송을 확인하거나 관계 기관이 요청한 경우 회사는 발송 기능을 정지하고 원장에게 지체 없이 알리며, 관련 자료를 한국인터넷진흥원 등 관계 기관에 제출할 수 있습니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제7장 의무와 이용 제한</h3>
    <Art n={26} title="회사의 의무">
      <ol>
        <li>회사는 법령과 이 약관이 금지하는 행위를 하지 않으며, 안정적인 서비스 제공과 개인정보 보호를 위해 노력합니다.</li>
        <li>설비에 장애가 생기면 지체 없이 복구하고, 복구에 시간이 걸리면 원인과 예상 시간을 알립니다.</li>
      </ol>
    </Art>
    <Art n={27} title="의견과 불만의 처리">
      <p>회사는 회원의 의견과 불만을 고객센터({config.supportEmail})와 1:1 문의로 접수하여 아래 기준으로 처리합니다. 기간 안에 끝내기 어려우면 사유와 일정을 알립니다.</p>
      <div className="table-wrap">
        <table className="table">
          <caption className="sr-only">불만 유형별 처리 기준</caption>
          <thead><tr><th scope="col">유형</th><th scope="col">처리</th><th scope="col">기간</th></tr></thead>
          <tbody>
            <tr><td>서비스 장애</td><td>복구, 원인과 재발 방지 안내</td><td>인지 후 지체 없이</td></tr>
            <tr><td>요금·결제 오류</td><td>확인 후 취소·환불</td><td>확인 후 3영업일 안</td></tr>
            <tr><td>개인정보 관련 요청</td><td>열람·정정·삭제 등 처리</td><td>접수 후 10일 안</td></tr>
            <tr><td>그 밖의 문의</td><td>답변</td><td>접수 후 3영업일 안</td></tr>
          </tbody>
        </table>
      </div>
    </Art>
    <Art n={28} title="회원의 의무">
      <p>회원은 다음 행위를 해서는 안 됩니다.</p>
      <ul>
        <li>타인의 정보 도용, 가입·변경 때 허위 정보 입력, 회사 직원이나 다른 회원 사칭</li>
        <li>업무 목적 외 이용자 정보 열람·반출, 다른 회원의 개인정보를 동의 없이 수집·공개하는 행위</li>
        <li>실제와 다른 출결·회기·결제 기록 입력</li>
        <li>서비스 운영 방해: 비정상 접근, 자동화 수집, 보안 우회, 악성 프로그램 배포, 다른 계정 권한 탈취</li>
        <li>회사의 허락 없이 서비스를 제3자에게 재판매·임대하는 행위</li>
        <li>회사나 제3자의 지식재산권·명예를 침해하는 행위</li>
        <li>그 밖에 법령을 위반하는 행위</li>
      </ul>
    </Art>
    <Art n={29} title="이용 제한">
      <ol>
        <li>회사는 회원이 법령이나 이 약관을 위반하면 위반의 내용·정도·횟수를 고려하여 경고, 기능 제한, 이용 정지를 할 수 있습니다.</li>
        <li>이용을 제한할 때에는 사유, 유형, 기간, 이의신청 방법을 미리 알립니다. 긴급한 경우에는 먼저 제한하고 지체 없이 알립니다.</li>
        <li>회사의 책임 있는 사유 없이 이루어진 정당한 이용 제한에 대해서는 회사가 손해를 배상하지 않습니다.</li>
      </ol>
    </Art>
    <Art n={30} title="조사를 위한 임시 제한">
      <ol>
        <li>회사는 계정 해킹·도용 신고가 접수되었거나 위법행위가 합리적으로 의심되면 조사가 끝날 때까지 해당 계정을 임시로 제한할 수 있습니다.</li>
        <li>조사 결과 회원에게 잘못이 없으면 제한된 기간만큼 유료 이용 기간을 연장합니다.</li>
      </ol>
    </Art>
    <Art n={31} title="이의신청">
      <p>회원은 이용 제한 통지를 받은 날부터 15일 안에 이메일 등으로 이의를 신청할 수 있습니다. 회사는 접수 후 15일 안에 답변하며, 그 안에 답변하기 어려우면 사유와 일정을 알립니다.</p>
    </Art>

    <h3 className="legal-chapter">제8장 계약의 해지</h3>
    <Art n={32} title="회원의 해지">
      <ol>
        <li>회원은 언제든 고객센터에 탈퇴를 요청할 수 있으며, 회사는 본인 확인 후 지체 없이 처리합니다.</li>
        <li>원장이 탈퇴하려면 센터의 유료서비스를 먼저 해지하거나 원장 권한을 넘겨야 합니다.</li>
      </ol>
    </Art>
    <Art n={33} title="회사의 해지">
      <ol>
        <li>회사는 다음의 경우 이용계약을 해지할 수 있습니다.
          <ul>
            <li>제6조 제1항의 사유가 있는 경우</li>
            <li>서비스를 불법적인 용도로 쓰거나 불법 정보를 유통한 경우</li>
            <li>제16조 제4항의 이용 제한 후 60일이 지나도록 요금을 내지 않은 경우</li>
            <li>이용 정지 후 1년 안에 같은 사유가 다시 생긴 경우</li>
            <li>관계 기관이 불법 메시지 발송을 확인하여 해지를 요청한 경우</li>
          </ul>
        </li>
        <li>해지할 때에는 사유와 해지일을 미리 알립니다. 긴급한 경우에는 해지 후 지체 없이 알립니다. 회원은 제31조에 따라 이의를 신청할 수 있습니다.</li>
        <li>환불은 환불 정책을 따릅니다.</li>
      </ol>
    </Art>
    <Art n={34} title="계약 종료 후의 자료">
      <ol>
        <li>계약이 끝나면 회사는 센터가 자료를 내려받을 수 있도록 30일간 보관한 뒤 지체 없이 파기합니다. 법령에 따라 보존해야 하는 기록은 그 기간만 분리 보관합니다.</li>
        <li>센터는 이 기간 안에 필요한 자료를 내려받아야 합니다.</li>
      </ol>
    </Art>

    <h3 className="legal-chapter">제9장 기타</h3>
    <Art n={35} title="손해배상">
      <p>회사 또는 회원이 이 약관을 위반하여 상대방에게 손해를 입히면 그 손해를 배상합니다. 다만 고의나 과실이 없는 경우에는 그렇지 않습니다.</p>
    </Art>
    <Art n={36} title="면책">
      <ol>
        <li>회사는 천재지변, 전쟁 등 불가항력으로 서비스를 제공할 수 없는 경우 책임을 지지 않습니다.</li>
        <li>회사는 회원의 책임 있는 사유로 생긴 이용 장애, 회원이 입력한 자료의 정확성, 회원 사이 또는 회원과 제3자 사이의 분쟁에 대해 책임을 지지 않습니다.</li>
        <li>제1항과 제2항에도 불구하고 회사의 고의 또는 중대한 과실로 생긴 손해에 대해서는 면책되지 않으며, 무료체험에도 같습니다.</li>
      </ol>
    </Art>
    <Art n={37} title="양도 금지">
      <p>회원은 이용계약상의 지위나 권리를 회사의 동의 없이 타인에게 양도·증여하거나 담보로 제공할 수 없습니다. 제22조 제3항의 센터 운영자 변경은 그 절차를 따릅니다.</p>
    </Art>
    <Art n={38} title="분쟁 해결">
      <ol>
        <li>회사는 회원의 불만을 제27조에 따라 처리합니다. 제3의 분쟁조정기관이 조정하는 경우 회사는 관련 사실을 성실히 소명합니다.</li>
        <li>회사와 회원 사이의 분쟁은 대한민국 법을 따르며, 소송은 「민사소송법」상 관할 법원에 제기합니다. 소비자인 회원과의 분쟁은 한국소비자원 등 분쟁조정기구에 조정을 신청할 수 있습니다.</li>
      </ol>
    </Art>
    <section className="legal-art"><h3>부칙</h3><p>이 약관은 {EFFECTIVE}부터 시행합니다.</p></section>
  </>;
}

function Tbl({ caption, head, rows }: { caption: string; head?: readonly string[]; rows: readonly (readonly ReactNode[])[] }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{caption}</caption>
        {head && <thead><tr>{head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>}
        <tbody>{rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Privacy() {
  const officer = config.privacyOfficer;
  return <>
    <p>{co()}(이하 "회사")는 「개인정보 보호법」 제30조에 따라 정보주체의 개인정보를 보호하고 고충을 빠르게 처리하기 위해 이 방침을 공개합니다. 이 방침은 발달재활·심리상담센터 통합 관리 서비스 SelfOn의 웹, 모바일 앱, PC 앱에 적용됩니다.</p>

    <h3 className="legal-chapter">한눈에 보기</h3>
    <Tbl caption="개인정보 처리 요약" head={['궁금한 것', '요약', '자세히']} rows={[
      ['무엇을 처리하나요', '계정 정보, 아동의 일정·과제·회기 기록, 결제 기록, 회원이 적은 할 일, 접속 기록', '제2조'],
      ['민감한 정보가 있나요', '장애유형·발달 상태와 회기 기록을 암호화해 저장합니다', '제6조'],
      ['AI가 쓰이나요', '선생님이 요청하고 보호자가 동의한 경우에만 쓰이며, AI 학습에는 쓰지 않습니다', '제7조'],
      ['해외로 나가나요', '서비스 데이터는 서울에 저장합니다. 동의한 AI 기능과 이메일 문의는 미국 등으로 전송될 수 있습니다', '제10조'],
      ['광고에 쓰나요', '쓰지 않습니다. 광고·행태 분석 도구도 쓰지 않습니다', '제16·17조'],
      ['어떻게 지우나요', '내 계정에서 탈퇴하거나 보호책임자에게 요청합니다', '제13조'],
    ]} />

    <h3 className="legal-chapter">회사와 센터의 역할</h3>
    <p>SelfOn의 정보는 누가 입력했는지에 따라 책임자가 다릅니다. 요청할 곳이 명확하지 않으면 회사로 알려 주세요. 센터 정보에 관한 요청은 회사가 센터에 전달합니다.</p>
    <Tbl caption="회사와 센터의 역할" head={['구분', '해당 정보', '개인정보처리자', '회사의 역할', '요청할 곳']} rows={[
      ['회사가 직접 처리', '회원 계정, 보호자가 등록한 자녀, 유료 결제, 선생님 찾기, 고객 문의, 할 일 관리', '회사', '처리자', '회사'],
      ['센터가 입력', '이용자 정보, 일정·출결, 수납, 회기 기록, 과제, 직원 급여, 결산', '해당 센터', '센터의 수탁자', '해당 센터 또는 회사'],
    ]} />

    <Art n={1} title="개인정보의 처리 목적과 법적 근거">
      <p>회사는 아래 목적에 필요한 범위에서만 개인정보를 처리합니다.</p>
      <Tbl caption="처리 목적과 법적 근거" head={['목적', '내용', '법적 근거']} rows={[
        ['회원 관리', '가입, 역할별 로그인, 아이디·비밀번호 찾기, 부정 이용 방지, 통지', '계약 이행, 동의'],
        ['센터 운영 지원', '일정·출결, 이용자, 수납, 바우처 대조, 결산, 급여, 수업 안내', '센터의 위탁'],
        ['치료 지원', '과제 제공·평가, 회기 기록, 보호자와 센터 연결', '센터의 위탁, 보호자 동의'],
        ['AI 기록 보조', '모니터링 초안, 회기 기록 초안, 음성 전사', '별도 동의'],
        ['선생님 찾기', '보호자와 선생님 연결, 개인 선생님 자격 검토', '동의'],
        ['할 일 관리', '회원 본인의 할 일 기록·정리, 원장이 소속 선생님에게 맡긴 일의 전달과 완료 알림', '계약 이행'],
        ['유료 서비스', '결제, 환불, 결제 내역 관리', '계약 이행, 법령상 의무'],
        ['민원과 법적 의무', '문의 응대, 접속기록 보관, 분쟁 대응', '법령상 의무, 정당한 이익'],
      ]} />
    </Art>

    <Art n={2} title="처리하는 개인정보 항목">
      <p><strong>회사가 직접 처리하는 정보</strong></p>
      <Tbl caption="회사가 직접 처리하는 항목" head={['구분', '수집 항목', '수집 방법']} rows={[
        ['공통 필수', '이름, 이메일, 휴대폰 번호, 비밀번호(복원할 수 없는 형태), 역할', '회원가입'],
        ['역할별 필수', '학생: 생년월일 / 선생님: 소속 센터 또는 개인 선생님 여부 / 원장: 센터 이름', '회원가입'],
        ['보호자의 자녀 등록', '자녀 이름, 생년월일, 연결된 센터, 동의·철회 기록', '보호자 입력'],
        ['결제 보호자 연결', '보호자 이름, 학생 이름, 연결 일시', '보호자·학생 입력'],
        ['선생님 찾기(선생님)', '이름, 직함, 분야, 수업 방식·지역, 소개, 수업료 / 개인 선생님: 자격 정보(회사 검토용, 보호자에게 비공개)', '선생님 입력'],
        ['선생님 찾기(보호자)', '신청한 선생님, 자녀 만 나이, 희망 요일·시간, 전할 말, 보호자 이름, 자녀 이름, 휴대폰 번호, 처리 상태', '보호자 입력'],
        ['유료 결제', '결제 금액·일시, 주문번호, 카드사, 가려진 카드번호, 승인번호, 정기결제 키(암호화 보관, 해지가 끝나면 삭제), 결제 알림 메일. 카드번호 전체는 회사가 받지 않습니다', '결제대행사, 원장 입력'],
        ['고객 문의', '이메일, 센터 정보, 문의와 답변 내용', '메일, 1:1 문의'],
        ['할 일 관리(암호화)', '할 일 제목·메모·체크리스트·태그·날짜, 프로젝트 이름·목표, 기다리는 대상, 주간 검토 일시, 맡긴 일의 맡긴 사람·받은 선생님 이름과 완료 여부. 회원이 적은 내용에 다른 사람의 정보가 들어갈 수 있으므로 되도록 적지 않도록 안내합니다', '회원 입력'],
        ['자동 생성', '접속 IP, 접속 일시, 이용 기록(감사 로그), 오류 기록', '서비스 이용 중 생성'],
      ]} />
      <p><strong>센터가 입력하고 회사가 위탁받아 처리하는 정보</strong></p>
      <Tbl caption="센터가 입력하는 항목" head={['구분', '항목']} rows={[
        ['이용자 기본', '이름, 출생연도, 담당 선생님, 회기 단가, 단기·장기 목표'],
        ['이용자 민감정보(암호화)', '장애유형·진단, 보호자 연락처, 주소, 상담 메모'],
        ['일정·출결·수납', '회기 일정, 출결과 사유, 청구·수납·환불 기록'],
        ['바우처 대조', '승인번호, 승인 일시, 금액. 올린 파일의 이름·생년월일은 브라우저에서만 읽고 서버로 보내지 않습니다'],
        ['치료 과제', '과제 내용, 수행 결과, 평가, 결과물 사진'],
        ['회기 기록(암호화)', '목표, 목표 측정값, 보호자·학생의 호소, 활동·반응·관찰, 평가, 다음 계획, 전사문, 수정 사유, 보호자 공유 문구'],
        ['회기 녹음', '음성과 전사문. 녹음은 기기에만 임시 보관하며, 전사를 요청하는 경우에만 서버를 거쳐 전송합니다. 원음은 서버에 저장하지 않습니다. 전사문은 선생님이 회기 기록에 넣어 저장한 경우에만 암호화해 보관합니다'],
        ['직원 급여(암호화)', '이름, 생년월일, 사번, 이메일, 입사일, 부서, 직위, 근로시간, 임금·공제 내역'],
        ['결산 회의록', '선생님 이름·코드·영역, 선생님별 수업 건수, 수입·지출, 변경 이력'],
        ['수강 신청', '신청한 수업, 보호자와 자녀 정보, 처리 결과와 거절 사유'],
      ]} />
    </Art>

    <Art n={3} title="보유 기간">
      <p>회사는 목적을 달성하면 개인정보를 지체 없이 파기합니다. 다만 아래 표의 정보는 적힌 기간 동안 보관합니다.</p>
      <Tbl caption="보유 기간" head={['정보', '보유 기간', '근거 또는 비고']} rows={[
        ['회원 정보', '탈퇴 시 지체 없이 파기', '회원 ID, 역할, 가입일, 탈퇴일은 기록용으로 남습니다'],
        ['보호자가 등록 해제한 자녀 정보', '즉시 내용 삭제, 남은 표시는 하루 뒤 삭제', '센터와 연결한 기록은 센터 기준을 따릅니다'],
        ['동의·철회 기록', '철회 또는 탈퇴 후 3년', '동의 사실 증명'],
        ['센터가 입력한 정보', '센터 계약 종료 후 자료 내려받기 기간 30일까지, 또는 센터가 삭제할 때까지', '회원이 탈퇴해도 센터 업무 기록은 센터 자료로 남습니다'],
        ['선생님 찾기 연락처·이름', '거절, 취소, 수업 종료, 탈퇴 시 지체 없이 파기', '나머지 신청 기록은 탈퇴 시까지'],
        ['할 일', '회원이 지우면 즉시 내용 삭제, 남은 표시는 하루 뒤 삭제. 탈퇴 시 지체 없이 파기', '맡긴 일의 사본은 받은 선생님이 지우거나 탈퇴할 때까지 그 계정에 남습니다. 받은 선생님이 탈퇴하면 끝나지 않은 일은 맡긴 원장에게 돌아갑니다'],
        ['연결 코드', '사용 기한(7일)이 지나고 하루 뒤 자동 삭제', '서비스 설정'],
        ['계약·청약철회 기록', '5년', '전자상거래법'],
        ['대금 결제·재화 공급 기록', '5년', '전자상거래법'],
        ['소비자 불만·분쟁 처리 기록', '3년', '전자상거래법'],
        ['로그인 기록', '3개월', '통신비밀보호법. 아래 접속기록과 함께 2년 이상 보관됩니다'],
        ['접속기록(감사 로그)', '2년 이상. 별도 보관소에서는 약 2년 1개월 후 삭제', '안전성 확보조치 기준. 위·변조할 수 없게 보관합니다'],
        ['서버 운영 로그', '약 2년', '이름·연락처 등은 가린 뒤 보관합니다'],
        ['백업', '일일 백업 35일, 월간 백업 90일', '장애 복구에만 씁니다. 삭제한 정보도 이 기간에는 백업에 남을 수 있습니다'],
        ['삭제한 과제 사진의 이전 판', '30일 후 자동 삭제', '서비스 설정'],
      ]} />
    </Art>

    <Art n={4} title="개인정보의 파기 절차와 방법">
      <ol>
        <li>절차: 회사는 파기 사유가 생긴 정보를 확인해 지체 없이 파기합니다. 법령에 따라 보존할 정보는 다른 저장 공간에 나누어 그 기간만 보관합니다.</li>
        <li>전자 파일: 복구할 수 없는 방법으로 삭제합니다.</li>
        <li>백업: 개별 항목을 골라 지우지 않고, 제3조의 기간이 지나면 자동으로 삭제합니다.</li>
        <li>종이 문서: 회사는 종이 문서로 개인정보를 보관하지 않습니다.</li>
        <li>위탁 종료: 수탁자와 계약이 끝나면 수탁자가 보관한 정보를 파기하도록 합니다.</li>
      </ol>
    </Art>

    <Art n={5} title="아동·청소년의 개인정보">
      <ol>
        <li>만 14세 미만 아동은 직접 가입할 수 없으며, 법정대리인인 보호자가 자녀로 등록합니다.</li>
        <li>회사는 보호자가 동의한 범위에서만 아동의 개인정보를 처리합니다. 보호자는 언제든 자녀 정보의 열람, 정정, 삭제를 요구하거나 동의를 철회할 수 있습니다.</li>
        <li>센터가 이용 아동의 정보를 입력할 때는 센터가 법정대리인의 동의를 받아야 합니다.</li>
        <li>만 14세 이상 만 19세 미만은 직접 가입할 수 있습니다. 보호자가 등록할 때는 자녀 본인의 동의를 확인합니다. 자녀는 보호자나 회사에 등록 해제와 정보 삭제를 요청할 수 있습니다.</li>
      </ol>
      <p><strong>어린이를 위한 설명:</strong> SelfOn은 선생님이 내 준 과제와 네가 올린 사진을 잘 보관해요. 사진과 기록은 네 선생님과 원장님, 부모님께서 필요한 만큼만 볼 수 있어요. 싫거나 걱정되는 일이 있으면 부모님이나 선생님께 말해 주세요. 지워 달라고 할 수도 있어요.</p>
    </Art>

    <Art n={6} title="민감정보와 사진·음성">
      <Tbl caption="민감정보와 사진·음성" head={['정보', '처리 조건', '보호 방법']} rows={[
        ['장애유형·진단 등 건강 정보', '별도 동의', '항목별 암호화. 담당 선생님과 원장만 열람하며 열람 기록이 남습니다'],
        ['회기 기록', '서비스 이용과 민감정보 동의', '암호화. 보호자에게는 선생님이 검토한 안내 문구만 공유합니다'],
        ['과제 결과물 사진', '사진 동의', '암호화 저장소에 보관합니다'],
        ['회기 음성', '녹음 동의. 전사는 국외 이전 동의를 추가로 확인', '참여자 안내 후 시작하며 언제든 중단할 수 있습니다'],
      ]} />
      <p>동의하지 않아도 해당 기능 외의 이용에는 영향이 없습니다. 다만 서비스 이용 동의를 철회하면 센터가 SelfOn에서 일정·출결·과제를 처리할 수 없습니다.</p>
      <p><strong>민감정보가 공개될 수 있는 경우:</strong> 선생님 찾기의 "전할 말"과 선생님 소개는 상대방이나 공개 화면에서 보일 수 있습니다. 아이의 진단명 등은 적지 마시고, 이미 적었다면 신청을 취소하거나 회사에 삭제를 요청해 주세요.</p>
    </Art>

    <Art n={7} title="치료 기록과 AI 이용 원칙">
      <ol>
        <li>목적 제한: 치료 기록은 해당 아동의 치료 지원과 센터 업무에만 씁니다. 광고나 회사의 상품 홍보에는 쓰지 않습니다.</li>
        <li>동의와 요청: AI는 보호자가 동의한 이용자에 대해 선생님이 요청할 때만 작동합니다.</li>
        <li>가림 처리의 한계: 회사는 등록된 이름과 연락처 형식을 자동으로 가립니다. 등록되지 않은 이름 등은 남을 수 있어, 회기 초안은 선생님이 전송 내용을 먼저 확인합니다. 음성 원음은 가릴 수 없습니다.</li>
        <li>모델 학습: 회사는 전송한 기록을 AI 모델 학습에 쓰지 않습니다.</li>
        <li>사람의 검토: AI 결과는 초안이며 선생님이 검토·수정한 뒤에만 기록으로 남습니다. 진단·처방·예후를 판단하는 도구가 아닙니다.</li>
        <li>운영자 접근: 본사 직원은 필요한 경우에만 사유를 남기고 센터 화면에 들어갈 수 있습니다. 이때는 민감정보를 열람하거나 개인정보를 내려받을 수 없습니다.</li>
      </ol>
    </Art>

    <Art n={8} title="개인정보의 제3자 제공">
      <p>회사는 정보주체의 동의가 있거나 법률에 특별한 규정이 있는 경우에만 개인정보를 제3자에게 제공합니다. 수사기관 등의 요청은 법적 근거와 절차를 확인한 뒤 필요한 범위에서만 응합니다.</p>
      <Tbl caption="제3자 제공" head={['제공받는 자', '목적', '항목', '보유 기간']} rows={[
        ['보호자가 연결 코드로 연결한 센터', '치료 과제 수행과 동의 확인', '자녀의 과제 수행 결과, 동의 현황', '연결 해제 또는 센터의 보유 기간까지'],
        ['보호자가 신청한 선생님(소속 센터가 있으면 공개 승인 범위의 원장 포함)', '수업 상담과 진행', '신청 시: 자녀 만 나이, 희망 요일·시간, 전할 말 / 수락 후: 보호자 이름, 자녀 이름, 휴대폰 번호', '신청이 거절·취소·종료될 때까지 화면 제공. 선생님은 수업이 끝나면 따로 보관한 정보를 파기'],
      ]} />
      <p>선생님 찾기 제공에 동의하지 않으면 신청할 수 없지만, 다른 서비스 이용에는 영향이 없습니다.</p>
    </Art>

    <Art n={9} title="개인정보 처리의 위탁">
      <p>회사는 서비스 제공에 필요한 업무를 아래와 같이 위탁합니다. 센터가 입력한 정보에 대해서는 이 표의 수탁자가 회사의 재수탁자가 됩니다.</p>
      <Tbl caption="수탁자" head={['수탁자', '위탁 업무', '처리 위치', '적용 범위']} rows={[
        ['Amazon Web Services, Inc.', '서버 운영, 데이터·파일 저장, 백업, 로그인 인증, 이메일 발송', '대한민국(서울 리전)', '전체 회원'],
        ['주식회사 페이플', '카드 결제 처리', '대한민국', '유료 결제 시'],
        ['OpenAI, L.L.C.', 'AI 초안 작성, 음성 전사', '미국', '동의한 경우'],
        ['Google (Google Workspace)', '고객 문의 메일 수신·보관', '미국 등 Google 데이터센터', '메일로 문의한 경우'],
      ]} />
      <p>회사는 위탁 계약에서 목적 외 처리 금지, 안전성 확보 조치, 재위탁 제한, 관리·감독, 손해배상을 정합니다. 수탁자나 위탁 업무가 바뀌면 이 방침을 고친 뒤 지체 없이 공개합니다. 알림톡 발송 대행사는 기능을 도입할 때 추가합니다.</p>
    </Art>

    <Art n={10} title="개인정보의 국외 이전">
      <p>서비스 데이터는 대한민국(서울)에 저장합니다. 아래 기능을 쓸 때만 개인정보가 국외로 전송됩니다.</p>
      <Tbl caption="AI 국외 이전" head={['구분', 'AI 모니터링 초안', 'AI 회기 기록 초안', '음성 전사']} rows={[
        ['이전받는 자', 'OpenAI, L.L.C. (privacy@openai.com)', '같음', '같음'],
        ['이전 국가', '미국', '미국', '미국'],
        ['이전 일시·방법', '선생님이 요청할 때마다 암호화 통신(HTTPS)으로 전송', '전송 내용을 선생님이 확인한 뒤 같은 방법으로 전송', '선생님이 확인하고 요청할 때 같은 방법으로 전송'],
        ['이전 항목', '자동으로 이름 등을 가린 과제 수행 기록과 서비스 목표', '자동으로 이름 등을 가린 목표, 전사문, 관찰 내용', '녹음 원음. 목소리와 대화 중 개인정보가 그대로 포함될 수 있습니다'],
        ['이전 목적', '월 서비스 계획 모니터링 초안 작성', '회기 기록 초안 작성', '한국어 전사문 작성'],
        ['보유 기간', 'OpenAI의 API 데이터 처리 조건에 따라 남용 감시 목적으로 최대 30일 보관 후 삭제하며 모델 학습에 쓰지 않습니다', '같음', '같음'],
        ['거부 방법', '보호자가 "우리 아이 > 동의 관리"에서 "AI 초안(국외 이전)"을 끄면 전송하지 않습니다', '"AI 회기 기록 초안(국외 이전)"을 끄면 전송하지 않습니다', '"음성 인식(국외 이전)"을 끄면 전송하지 않습니다'],
        ['거부의 영향', '해당 기능만 쓸 수 없고 선생님이 직접 작성합니다', '같음', '같음'],
      ]} />
      <p>이메일로 문의하면 메일 주소와 문의 내용이 Google(미국 등, policies.google.com/privacy)에 암호화 통신으로 전송되어 문의 처리를 위해 보관되며, 보유 기간은 제3조의 소비자 불만·분쟁 처리 기록을 따릅니다. 원하지 않으면 전화로 문의할 수 있습니다.</p>
      <p>결제창을 열 때 결제 화면에 필요한 라이브러리를 Google 서버에서 받으며, 이때 접속 IP가 Google에 전달될 수 있습니다.</p>
    </Art>

    <Art n={11} title="추가적 이용·제공 기준">
      <p>회사는 당초 목적과 관련이 있고, 정보주체가 예상할 수 있으며, 정보주체의 이익을 부당하게 침해하지 않고, 암호화 등 필요한 조치를 한 경우에만 동의 없이 추가로 이용·제공할 수 있습니다. 민감정보, 회기 기록, 아동의 정보는 이 기준으로 추가 이용하지 않습니다.</p>
    </Art>

    <Art n={12} title="자동화된 결정">
      <p>회사는 완전히 자동화된 처리만으로 정보주체의 권리나 의무에 중대한 영향을 주는 결정을 하지 않습니다. 바우처 대조의 위험 점수는 센터가 확인할 항목을 표시하는 자료이며, 판단은 센터와 관계 기관의 확인 절차로 합니다. AI 초안도 담당 선생님이 검토한 뒤에만 기록으로 남습니다.</p>
    </Art>

    <Art n={13} title="정보주체와 법정대리인의 권리">
      <p>정보주체는 언제든 개인정보의 열람, 정정·삭제, 처리정지, 동의 철회를 요구할 수 있습니다. 만 14세 미만 아동은 법정대리인이 행사합니다.</p>
      <Tbl caption="권리 행사 방법" head={['하려는 일', '방법']} rows={[
        ['동의 변경·철회', '보호자: 우리 아이 > 동의 관리'],
        ['자녀 정보 삭제', '보호자: 우리 아이 > 등록 해제. 센터와 연결된 경우 연결 해제가 먼저 필요합니다'],
        ['회원 탈퇴', '내 계정 > 회원 탈퇴. 센터가 있는 원장은 고객센터로 문의합니다'],
        ['그 밖의 열람·정정·처리정지', '개인정보 보호책임자에게 이메일 또는 전화로 요청합니다'],
        ['센터가 입력한 정보', '해당 센터에 요청합니다. 회사에 요청하면 센터에 지체 없이 전달합니다'],
      ]} />
      <p>회사는 본인인지 확인한 뒤 10일 이내에 처리하고 결과를 알립니다. 법령에 따라 보존해야 하는 정보는 삭제할 수 없으며, 요청을 거절하면 그 사유와 이의 제기 방법을 알립니다.</p>
    </Art>

    <Art n={14} title="개인정보의 안전성 확보 조치">
      <Tbl caption="안전성 확보 조치" head={['구분', '조치']} rows={[
        ['관리적', '역할별 최소 권한, 본사 대리접속의 사유 입력·시간 제한·전체 기록, 사고 대응 절차 마련'],
        ['기술적', '전송 구간 암호화, 민감정보·회기 기록·급여 암호화, 비밀번호 단방향 저장, 2단계 인증 지원, 센터별 데이터 분리, 30분 무조작 자동 로그아웃, 개인정보 다운로드 사유 기록과 이름 가림, 로그의 개인정보 가림, 보안 헤더'],
        ['접속기록', '개인정보 처리 기록을 2년 이상 위·변조할 수 없게 보관'],
        ['물리적', 'Amazon Web Services 데이터센터(서울)에 저장하며 회사 사무실에 보관하지 않음'],
      ]} />
    </Art>

    <Art n={15} title="개인정보 유출 시 조치">
      <p>개인정보 유출 등을 알게 되면 회사는 법령이 정한 기간 안에 유출 항목, 시점과 경위, 피해 최소화 방법, 회사의 조치, 신고·상담 창구를 정보주체에게 알리고 관계 기관에 신고합니다. 센터가 입력한 정보라면 해당 센터에도 지체 없이 알립니다.</p>
    </Art>

    <Art n={16} title="자동 수집 장치와 앱 권한">
      <p>회사는 광고·행태 분석용 쿠키나 추적 도구를 쓰지 않습니다.</p>
      <Tbl caption="자동 수집 장치와 앱 권한" head={['장치·권한', '용도', '관리 방법']} rows={[
        ['브라우저 세션 저장소', '로그인 유지', '탭을 닫으면 지워집니다'],
        ['브라우저 저장소', '테마, 달력 보기, 패널 크기 같은 화면 설정', '그 기기에만 남으며 브라우저 설정에서 지울 수 있습니다'],
        ['카메라·사진 보관함(모바일 앱)', '과제 결과물 사진 제출', '기기 설정에서 언제든 끌 수 있습니다'],
        ['마이크(웹)', '회기 녹음', '브라우저 권한에서 끌 수 있습니다. 모바일 앱은 현재 마이크를 쓰지 않습니다'],
      ]} />
    </Art>

    <Art n={17} title="광고성 정보">
      <p>회사는 현재 광고성 정보를 보내지 않습니다. 보내려면 미리 별도 동의를 받고 수신 거부 방법을 함께 안내합니다. 전해 드리는 서비스 공지와 일정 알림은 광고가 아닙니다.</p>
    </Art>

    <Art n={18} title="개인정보 보호책임자">
      <Tbl caption="개인정보 보호책임자" rows={[
        ['성명·직위', `${officer.name}, ${officer.title}`],
        ['이메일', <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>],
        ['전화', BUSINESS.phone],
      ]} />
      <p>열람 청구, 처리 관련 문의, 불만, 피해 구제는 위 연락처로 접수합니다.</p>
    </Art>

    <Art n={19} title="권익침해 구제 방법">
      <ul>
        <li>개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)</li>
        <li>개인정보침해신고센터: 국번 없이 118 (privacy.kisa.or.kr)</li>
        <li>대검찰청 사이버수사과: 국번 없이 1301 (www.spo.go.kr)</li>
        <li>경찰청 사이버수사국: 국번 없이 182 (ecrm.police.go.kr)</li>
      </ul>
    </Art>

    <Art n={20} title="영업 양수도와 서비스 종료">
      <p>영업 양도 등으로 개인정보를 이전하면 미리 이전 사실과 받는 자의 연락처, 이전을 원하지 않을 때의 방법을 알립니다. 서비스를 종료하면 30일 전에 알리고, 센터가 자료를 내려받을 기간을 둔 뒤 파기합니다.</p>
    </Art>

    <Art n={21} title="방침의 변경">
      <p>이 방침은 {EFFECTIVE}부터 적용합니다. 내용이 바뀌면 시행 7일 전에, 중요한 변경은 30일 전에 서비스 공지로 알립니다. 이전 방침은 아래 이력에서 볼 수 있습니다.</p>
      <Tbl caption="개정 이력" head={['버전', '시행일', '주요 변경']} rows={[
        ['1', `${EFFECTIVE} (초안)`, '최초 작성'],
      ]} />
    </Art>
  </>;
}
